//---------------------------------------------------------
// One tile per OAM pair record (UX lock #51). Settings and Delete only:
// nothing on the tile powers or reboots a node.
//---------------------------------------------------------
import {Box, Card, CardContent, Divider, Stack, Typography} from '@mui/material';
import {get_date} from 'common';
import SimpleButton from 'components/element/SimpleButton';
import {deletePair} from 'connector/oam/haPairApi';
import {LINODE_PAUSED_TOOLTIP, useLinodePaused} from 'hooks/useLinodeMutate';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {FONT_MONO} from 'theme';
import type {HAPairListItem} from 'types/haPair';
import HAConfirmDialog from './HAConfirmDialog';
import {FailedOverChip, HAChip, PairHealthChip} from './HAChips';
import {deleteDialogBody, failedOverChip, partialDeleteMessage} from './haCopy';

export function pairDetailPath(pairId: string): string {
	return `/soft/pairs/${encodeURIComponent(pairId)}`;
}

export default function HAPairCard(props: {pair: HAPairListItem; canDelete: boolean; onDeleted?: () => void}) {
	const {pair, canDelete, onDeleted} = props;
	const linodePaused = useLinodePaused();
	const navigate = useNavigate();
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [busy, setBusy] = useState(false);
	const [err, setErr] = useState<string | null>(null);

	const onDelete = async () => {
		setBusy(true);
		setErr(null);
		const r = await deletePair(pair.pairId);
		setBusy(false);
		if (r.status === 204 || r.status === 200) {
			setConfirmOpen(false);
			onDeleted?.();
			return;
		}
		const sv = (r.data as any)?.survivor;
		if (r.status === 502 && sv && (sv.node === 1 || sv.node === 2)) {
			// Partial delete keeps the tile and names the node.
			setErr(partialDeleteMessage(sv.node, sv.linodeId));
			onDeleted?.();
			return;
		}
		setErr(r.error?.message ? `${r.error.message.replace(/\.+$/, '')}.` : 'Delete failed.');
	};

	const failover = failedOverChip(pair.failedOver);
	return (
		<Card data-testid="ha-pair-tile" sx={{width: '260px', height: '500px', cursor: 'pointer'}} elevation={3} onClick={() => navigate(pairDetailPath(pair.pairId))}>
			<CardContent>
				<Stack gap="8px">
					<Box display="flex" justifyContent="space-between" data-testid="ha-tile-created-row">
						<Typography variant="caption" color="text.secondary">
							Created at
						</Typography>
						<Typography variant="caption" color="text.disabled">
							{pair.createdAt ? get_date(pair.createdAt) : '—'}
						</Typography>
					</Box>
					<Divider />
					<Box width="100%" display="flex" alignItems="center" gap="8px" flexWrap="wrap" data-testid="ha-tile-name-row">
						<Typography variant="subtitle1" color="textSecondary" sx={{whiteSpace: 'normal', overflowWrap: 'normal', wordBreak: 'normal'}}>
							{pair.name}
						</Typography>
						<HAChip />
					</Box>

					<Box width="100%" display="flex" alignItems="center" justifyContent="space-between" data-testid="ha-tile-id-row">
						<Box display="flex" alignItems="center" gap="8px">
							<Typography variant="caption" color="text.secondary">
								ID
							</Typography>
							<Typography variant="body2" sx={{fontFamily: FONT_MONO}} data-testid="ha-tile-id">
								{pair.pairId}
							</Typography>
						</Box>
						<Box display="flex" alignItems="center" justifyContent="flex-end" data-testid="ha-tile-actions" onClick={e => e.stopPropagation()}>
							<SimpleButton type="modify" tooltip="Settings" onClick={() => navigate(pairDetailPath(pair.pairId))} />
							{canDelete && (
								<SimpleButton
									type="delete_strong"
									tooltip={linodePaused ? LINODE_PAUSED_TOOLTIP : 'Delete'}
									disabled={linodePaused}
									onClick={() => {
										setErr(null);
										setConfirmOpen(true);
									}}
								/>
							)}
						</Box>
					</Box>

					<Divider />

					<Box display="flex" justifyContent="space-between">
						<Typography variant="caption" color="text.secondary">
							Host
						</Typography>
						<Typography variant="caption" sx={{fontFamily: FONT_MONO}} data-testid="ha-tile-host">
							{pair.host}
						</Typography>
					</Box>

					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="caption" color="text.secondary">
							Health Status
						</Typography>
						<PairHealthChip health={pair.health} />
					</Box>
					{failover && (
						<Box display="flex" justifyContent="flex-end">
							<FailedOverChip />
						</Box>
					)}
					{err && !confirmOpen && (
						<Typography variant="caption" color="error" data-testid="ha-tile-delete-error">
							{err}
						</Typography>
					)}
				</Stack>
			</CardContent>
			<Box onClick={e => e.stopPropagation()}>
				<HAConfirmDialog
					open={confirmOpen}
					title="Delete pair?"
					body={deleteDialogBody(pair.name)}
					confirm="Delete"
					destructive
					busy={busy}
					error={err}
					onConfirm={onDelete}
					onClose={() => setConfirmOpen(false)}
				/>
			</Box>
		</Card>
	);
}
